import React, { useState } from "react";
import ShadowCard from "../../../components/common/ShadowCard";
import {
    FaBed, FaBus, FaCalendarAlt, FaClock, FaUsers,
    FaInfoCircle, FaTicketAlt,
} from "react-icons/fa";
import { Typography } from "@progress/kendo-react-common";
import IconComponent from "../../../components/common/IconComponent";
import moment from "moment";
import "../b2bmodule.scss";

interface QuotationSummaryProps {
    values: any;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const fmt = (n: number) => Number(n || 0).toLocaleString("en-IN");

const getHotelTotal = (hotels: any[]) =>
    (hotels || []).reduce((acc: number, h: any) => {
        const noOfRooms = parseFloat(h.NoOfRooms) || 1;
        return acc + (h.NightPrices || []).reduce((p: number, np: any) =>
            p + (noOfRooms * (parseFloat(np.GivenPrice) || 0)), 0
        );
    }, 0);

const getInclusionsTotal = (inclusions: any[]) =>
    (inclusions || []).reduce((acc: number, i: any) => acc + (parseFloat(i?.TotalPrice) || 0), 0);

const formatDuration = (totalMinutes: number) => {
    if (!totalMinutes || totalMinutes <= 0) return "";

    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;

    const parts = [];
    if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`);
    if (hours > 0) parts.push(`${hours}hr${hours > 1 ? "s" : ""}`);
    if (minutes > 0) parts.push(`${minutes} mins`);

    return parts.join(" ");
};

const getTransportActivityTotals = (values: any) => {
    let transports = 0;
    let activities = 0;
    (values?.TransportsAndActivities?.ServiceGroups || []).forEach((group: any) => {
        (group.Services || []).forEach((svc: any) => {
            let svcTotal = 0;
            const subservices = (svc.Subservices || svc.Items || []);
            subservices.forEach((sub: any) => {
                const qty = parseFloat(sub.Qty) || 0;
                (sub.DailyPrices || []).forEach((dp: any) => {
                    svcTotal += (qty * (parseFloat(dp.GivenPrice) || 0));
                });
            });
            const type = (svc.Type || "").toUpperCase();
            if (type === "ACTIVITY") activities += svcTotal;
            else transports += svcTotal;
        });
    });
    return { transports, activities };
};

const getExtraTransTotal = (values: any) =>
    (values?.TransportsAndActivities?.ExtraTransportationServices || []).reduce(
        (acc: number, e: any) => acc + (parseFloat(e?.TotalPrice) || 0), 0
    );

const getOtherSpecialTotal = (values: any) =>
    (values?.OtherSpecialServices || []).reduce(
        (acc: number, s: any) => acc + (parseFloat(s?.TotalPrice) || 0), 0
    );

// ─────────────────────────────────────────────────────────────────────────────
// TotalBar — the coloured totals row at the bottom of summary
// ─────────────────────────────────────────────────────────────────────────────
const TotalBar = ({ hotels, transports, activities, extras, total }: any) => (
    <div className="summary-total-bar-unified">
        {[
            { label: "Hotels", value: hotels, color: "#4267b2" },
            { label: "Transport", value: transports, color: "#0891b2" },
            { label: "Activities", value: activities, color: "#7c3aed" },
            { label: "Extras", value: extras, color: "#d97706" },
            { label: "Total", value: total, color: "#16a34a", highlight: true },
        ].map((item, i) => (
            <div
                key={i}
                className={`total-bar-item ${item.highlight ? 'highlight' : ''}`}
            >
                <div className="label">
                    {item.label}
                </div>
                <div className="value" style={{ color: !item.highlight ? item.color : undefined }}>
                    ₹{fmt(item.value)}
                </div>
            </div>
        ))}
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// HotelSummaryTable
// ─────────────────────────────────────────────────────────────────────────────
const HotelSummaryTable = ({ hotels, inclusions }: { hotels: any[]; inclusions: any[] }) => {
    const hotelTotal = getHotelTotal(hotels);
    const inclTotal = getInclusionsTotal(inclusions);

    return (
        <div className="summary-section-container">
            <div className="summary-section-header">
                <div className="icon-circle hotel">
                    <IconComponent icon={FaBed} />
                </div>
                <span className="section-label">
                    Hotel Accommodations
                </span>
            </div>

            <div className="summary-table-container">
                <table className="summary-table-unified">
                    <thead>
                        <tr>
                            {["Night / Date", "Hotel", "Meal Plan", "Room Type", "Rooms", "Price"].map((h, i) => (
                                <th key={i} style={{ textAlign: i >= 4 ? "center" : "left" }}>
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {(hotels || []).length === 0 ? (
                            <tr>
                                <td colSpan={6} className="summary-no-data">
                                    No hotels selected
                                </td>
                            </tr>
                        ) : (
                            (hotels || []).flatMap((h: any, hIdx: number) =>
                                (h.NightPrices || []).map((p: any, pIdx: number) => (
                                    <tr key={`${hIdx}-${pIdx}`}>
                                        <td>
                                            <div className="night-badge">
                                                Night {(p._label || h.Nights?.[pIdx] || "").match(/\d+/)?.[0] || (pIdx + 1)}
                                            </div>
                                            <div className="date-text">
                                                {p.Date ? moment(p.Date).format("ddd, DD MMM") : ""}
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ fontWeight: 700, color: "#212529" }}>{h.HotelName || "—"}</div>
                                        </td>
                                        <td>
                                            <span className="meal-badge">
                                                {h.MealPlanName || "EP"}
                                            </span>
                                        </td>
                                        <td className="date-text">
                                            {h.RoomTypeName || "N/A"}
                                        </td>
                                        <td style={{ textAlign: "center" }}>
                                            <div style={{ fontWeight: 700, color: "#212529" }}>{h.NoOfRooms || 1}</div>
                                            <div className="date-text">{h.Adults || 0} pax</div>
                                        </td>
                                        <td style={{ textAlign: "right" }}>
                                            <div className="price-text hotel">
                                                ₹{fmt((parseFloat(h.NoOfRooms) || 1) * (parseFloat(p.GivenPrice) || 0))}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )
                        )}
                    </tbody>
                </table>

                {inclusions.length > 0 && (
                    <>
                        <div className="summary-extra-header">
                            <span className="extra-badge">
                                EXTRA HOTEL INCLUSION
                            </span>
                        </div>
                        <table className="summary-table-unified">
                            <tbody>
                                {inclusions.map((incl: any, iIdx: number) => (
                                    <tr key={iIdx}>
                                        <td style={{ width: "100px" }}>
                                            <span className="summary-badge hotel" style={{ background: "rgba(8,145,178,0.1)", color: "#0891b2" }}>
                                                Night {(String(incl.Night || "")).match(/\d+/)?.[0] || "—"}
                                            </span>
                                        </td>
                                        <td>
                                            <span style={{ fontWeight: 700, color: "#212529", fontSize: "13px" }}>
                                                {incl.ServiceName || incl.ServiceID || incl.Service || "Special Inclusion"}
                                            </span>
                                        </td>
                                        <td className="date-text" colSpan={3}>
                                            {incl.Comment || ""}
                                        </td>
                                        <td style={{ padding: "10px 14px", textAlign: "right" }}>
                                            <span className="price-text hotel" style={{ color: "#0891b2" }}>
                                                ₹{fmt(parseFloat(incl.TotalPrice) || 0)}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}

                {/* Subtotal */}
                <div className="summary-subtotal-row">
                    <span className="label">
                        Subtotal — Hotels &amp; Inclusions
                    </span>
                    <span className="value hotel">
                        ₹{fmt(hotelTotal + inclTotal)}
                    </span>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// TransportSummaryTable
// ─────────────────────────────────────────────────────────────────────────────
const TransportSummaryTable = ({ values }: { values: any }) => {
    const serviceGroups = values?.TransportsAndActivities?.ServiceGroups || [];
    const extraServices = values?.TransportsAndActivities?.ExtraTransportationServices || [];
    const otherSpecial = values?.OtherSpecialServices || [];

    let transportSubtotal = 0;
    let activitySubtotal = 0;
    serviceGroups.forEach((group: any) => {
        (group.Services || []).forEach((svc: any) => {
            let svcTotal = 0;
            const subservices = (svc.Subservices || svc.Items || []);
            subservices.forEach((sub: any) => {
                const qty = parseFloat(sub.Qty) || 0;
                (sub.DailyPrices || []).forEach((dp: any) => {
                    svcTotal += (qty * (parseFloat(dp.GivenPrice) || 0));
                });
            });
            if ((svc.Type || "").toUpperCase() === "ACTIVITY") activitySubtotal += svcTotal;
            else transportSubtotal += svcTotal;
        });
    });

    const extraTotal = extraServices.reduce((a: number, e: any) => a + (parseFloat(e?.TotalPrice) || 0), 0);
    const otherTotal = otherSpecial.reduce((a: number, s: any) => a + (parseFloat(s?.TotalPrice) || 0), 0);

    const hasAny = serviceGroups.length > 0 || extraServices.length > 0 || otherSpecial.length > 0;

    return (
        <div className="summary-section-container">
            <div className="summary-section-header">
                <div className="icon-circle transport">
                    <IconComponent icon={FaBus} />
                </div>
                <span className="section-label">
                    Transports, Activities &amp; Extras
                </span>
            </div>

            <div className="summary-table-container">
                {!hasAny ? (
                    <div className="summary-no-data">
                        No transportation or activities added yet
                    </div>
                ) : (
                    <>
                        {/* Service Groups */}
                        {serviceGroups.map((group: any, gIdx: number) => {
                            const stayDays = group.StayDays || [];
                            const dayLabel = stayDays.length > 0
                                ? stayDays.map((sd: any) => {
                                    const opt = sd.Date ? moment(sd.Date).format("DD MMM") : `Day ${sd.Day}`;
                                    return `Day ${sd.Day} (${opt})`;
                                }).join(", ")
                                : `Group ${gIdx + 1}`;

                            return (
                                <div key={gIdx}>
                                    <div className="summary-day-header">
                                        <IconComponent icon={FaCalendarAlt} className="text-muted opacity-50" style={{ fontSize: "12px" }} />
                                        <span className="label">
                                            {dayLabel}
                                        </span>
                                    </div>

                                    <table className="summary-table-unified">
                                        <thead>
                                            <tr>
                                                {["Type", "Service", "Vehicle / Category", "Qty", "Days", "Price"].map((h, i) => (
                                                    <th key={i} style={{ textAlign: i >= 3 ? "center" : "left" }}>
                                                        {h}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(group.Services || []).length === 0 ? (
                                                <tr>
                                                    <td colSpan={6} className="summary-no-data">
                                                        No services for this group
                                                    </td>
                                                </tr>
                                            ) : (
                                                (group.Services || []).flatMap((svc: any, sIdx: number) =>
                                                    (svc.Subservices || []).flatMap((sub: any, subIdx: number) =>
                                                        (sub.DailyPrices || [{ GivenPrice: 0 }]).map((dp: any, dpIdx: number) => {
                                                            const isFirst = subIdx === 0 && dpIdx === 0;
                                                            const isActivity = (svc.Type || "").toUpperCase() === "ACTIVITY";
                                                            const rowSpan = (svc.Subservices || []).reduce(
                                                                (acc: number, s: any) => acc + Math.max((s.DailyPrices || [1]).length, 1), 0
                                                            );
                                                            return (
                                                                <tr key={`${sIdx}-${subIdx}-${dpIdx}`}>
                                                                    {isFirst && (
                                                                        <td rowSpan={rowSpan} style={{ verticalAlign: "top", borderRight: "1px solid #f1f3f5" }}>
                                                                            <span className={`summary-badge ${isActivity ? 'activity' : 'transport'}`}>
                                                                                <IconComponent icon={isActivity ? FaTicketAlt : FaBus} style={{ fontSize: "9px" }} />
                                                                                {svc.Type || "TRANSPORT"}
                                                                            </span>
                                                                        </td>
                                                                    )}
                                                                    {isFirst && (
                                                                        <td rowSpan={rowSpan} style={{ verticalAlign: "top", borderRight: "1px solid #f1f3f5" }}>
                                                                            <div style={{ fontWeight: 700, color: "#212529", fontSize: "13px" }}>
                                                                                {svc.ServiceName || svc.ServiceID || "—"}
                                                                            </div>
                                                                            {(svc.ServiceTypeName || svc.ServiceTypeID) && (
                                                                                <div className="date-text">{svc.ServiceTypeName || svc.ServiceTypeID}</div>
                                                                            )}
                                                                            {isActivity && (svc.Slot || (Number(svc.DurationMinutes) > 0)) && (
                                                                                <div className="date-text" style={{ color: "#7c3aed", marginTop: "4px", display: "flex", gap: "8px", fontWeight: 600 }}>
                                                                                    {svc.Slot && (
                                                                                        <span className="d-inline-flex align-items-center gap-1">
                                                                                            <IconComponent icon={FaClock} style={{ fontSize: "9px" }} />
                                                                                            {svc.Slot}
                                                                                        </span>
                                                                                    )}
                                                                                    {Number(svc.DurationMinutes) > 0 && (
                                                                                        <span>{formatDuration(Number(svc.DurationMinutes))}</span>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                    )}
                                                                    {dpIdx === 0 && (
                                                                        <td rowSpan={(sub.DailyPrices || [1]).length} style={{ verticalAlign: "top", borderRight: "1px solid #f1f3f5", color: "#495057", fontSize: "12px" }}>
                                                                            {sub.TypeName || sub.TypeID || "—"}
                                                                        </td>
                                                                    )}
                                                                    {dpIdx === 0 && (
                                                                        <td rowSpan={(sub.DailyPrices || [1]).length} style={{ textAlign: "center", verticalAlign: "top", borderRight: "1px solid #f1f3f5" }}>
                                                                            <span className="meal-badge">
                                                                                ×{sub.Qty || 1}
                                                                            </span>
                                                                        </td>
                                                                    )}
                                                                    <td className="date-text" style={{ textAlign: "center", borderRight: "1px solid #f1f3f5" }}>
                                                                        {dp.Date ? moment(dp.Date).format("DD MMM") : "—"}
                                                                    </td>
                                                                    <td style={{ textAlign: "right" }}>
                                                                        <span className={`price-text ${isActivity ? 'activity' : 'transport'}`}>
                                                                            ₹{fmt((parseFloat(sub.Qty) || 0) * (parseFloat(dp.GivenPrice) || 0))}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    )
                                                )
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            );
                        })}

                        {/* Extra Transportation */}
                        {extraServices.length > 0 && (
                            <>
                                <div className="summary-extra-header">
                                    <span className="extra-badge" style={{ background: "#d97706" }}>
                                        EXTRA TRANSPORTATION
                                    </span>
                                </div>
                                <table className="summary-table-unified">
                                    <tbody>
                                        {extraServices.map((e: any, eIdx: number) => (
                                            <tr key={eIdx}>
                                                <td style={{ width: "80px" }}>
                                                    <span className="summary-badge transport">
                                                        Day {e.Day || "—"}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span style={{ fontWeight: 700, color: "#212529", fontSize: "13px" }}>
                                                        {e.ServiceName || e.ServiceID || "Extra Service"}
                                                    </span>
                                                </td>
                                                <td className="date-text" colSpan={3}>
                                                    {e.Comment || ""}
                                                </td>
                                                <td style={{ textAlign: "right" }}>
                                                    <span className="price-text transport" style={{ color: "#d97706" }}>
                                                        ₹{fmt(parseFloat(e.TotalPrice) || 0)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}

                        {/* Other Special Services */}
                        {otherSpecial.length > 0 && (
                            <>
                                <div className="summary-extra-header">
                                    <span className="extra-badge" style={{ background: "#7c3aed" }}>
                                        OTHER SPECIAL SERVICES
                                    </span>
                                </div>
                                <table className="summary-table-unified">
                                    <tbody>
                                        {otherSpecial.map((s: any, sIdx: number) => (
                                            <tr key={sIdx}>
                                                <td style={{ width: "100px" }}>
                                                    <span className="summary-badge activity">
                                                        Day {(String(s.DayNo || "")).match(/\d+/)?.[0] || "—"}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span style={{ fontWeight: 700, color: "#212529", fontSize: "13px" }}>
                                                        {s.ServiceName || s.ServiceID || s.Service || "Special Service"}
                                                    </span>
                                                </td>
                                                <td colSpan={3} className="date-text">
                                                    {s.Comment || ""}
                                                </td>
                                                <td style={{ textAlign: "right" }}>
                                                    <span className="price-text activity">
                                                        ₹{fmt(parseFloat(s.TotalPrice) || 0)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}
                    </>
                )}

                {/* Subtotal Row */}
                <div className="summary-subtotal-row">
                    <div style={{ display: "flex", gap: "24px" }}>
                        <span className="label" style={{ color: "#6c757d" }}>
                            Transport: <strong style={{ color: "#0891b2" }}>₹{fmt(transportSubtotal)}</strong>
                        </span>
                        <span className="label" style={{ color: "#6c757d" }}>
                            Activities: <strong style={{ color: "#7c3aed" }}>₹{fmt(activitySubtotal)}</strong>
                        </span>
                        <span className="label" style={{ color: "#6c757d" }}>
                            Extras: <strong style={{ color: "#d97706" }}>₹{fmt(extraTotal + otherTotal)}</strong>
                        </span>
                    </div>
                    <span className="value transport">
                        ₹{fmt(transportSubtotal + activitySubtotal + extraTotal + otherTotal)}
                    </span>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// OptionSummaryPanel — one tab panel per PackageOption
// ─────────────────────────────────────────────────────────────────────────────
const OptionSummaryPanel = ({ opt, values }: { opt: any; values: any }) => {
    const hotelTotal = getHotelTotal(opt.Hotels);
    const inclTotal = getInclusionsTotal(opt.HotelSpecialInclusions);
    const { transports, activities } = getTransportActivityTotals(values);
    const extraTrans = getExtraTransTotal(values);
    const otherSpec = getOtherSpecialTotal(values);
    const extras = extraTrans + otherSpec;
    const grandTotal = hotelTotal + inclTotal + transports + activities + extras;

    return (
        <div style={{ padding: "20px" }}>
            <HotelSummaryTable hotels={opt.Hotels} inclusions={opt.HotelSpecialInclusions} />
            <TransportSummaryTable values={values} />
            <TotalBar
                hotels={hotelTotal + inclTotal}
                transports={transports}
                activities={activities}
                extras={extras}
                total={grandTotal}
            />
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main QuotationSummary
// ─────────────────────────────────────────────────────────────────────────────
const QuotationSummary: React.FC<QuotationSummaryProps> = ({ values }) => {
    const [activeTab, setActiveTab] = useState(0);
    if (!values) return null;

    const packageOptions = values.PackageOptions || [];
    const nights = Number(values.Nights) || 0;
    const startDate = values.TripStartDate ? moment(values.TripStartDate).format("DD MMM, YYYY") : "—";
    const adults = values.Adults || 0;
    const children = values.Children || 0;
    const infants = values.Infants || 0;

    return (
        <ShadowCard className="b2b-quotation-module b2b-card mb-4 p-0" style={{ borderRadius: "10px", overflow: "hidden" }}>

            {/* ── Header ── */}
            <div className="p-4 d-flex gap-3 align-items-start border-bottom border-light">
                <div className="hotel-icon-wrap shadow-sm">
                    <IconComponent icon={FaInfoCircle} />
                </div>
                <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <div className="d-flex align-items-center gap-2">
                                <Typography.h5 className="section-title mb-0">Quotation Summary</Typography.h5>
                            </div>
                            <Typography.p className="section-subtitle mb-0 mt-1" style={{ fontSize: "13px" }}>
                                Live snapshot of your complete package
                            </Typography.p>
                        </div>
                        {/* Quick stats */}
                        <div className="header-stats d-flex gap-3">
                            {[
                                { icon: FaCalendarAlt, val: startDate },
                                { icon: FaClock, val: `${nights}N / ${nights + 1}D` },
                                { icon: FaUsers, val: `${adults} Adults${children > 0 ? ` + ${children} Ch` : ""}${infants > 0 ? ` + ${infants} Inf` : ""}` },
                            ].map((item, i) => (
                                <div key={i} className="stat-item d-flex align-items-center gap-2 px-3 py-1 bg-light rounded-pill border border-light-subtle shadow-sm" style={{ fontSize: "11px", fontWeight: 600, color: "#4b5563" }}>
                                    <IconComponent icon={item.icon} style={{ fontSize: "12px", color: "#6b7280" }} />
                                    <span>{item.val}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Package Option Tabs ── */}
            {packageOptions.length > 1 && (
                <div className="summary-tabs-container">
                    {packageOptions.map((opt: any, idx: number) => (
                        <button
                            key={idx}
                            type="button"
                            onClick={() => setActiveTab(idx)}
                            className={`summary-tab-btn ${activeTab === idx ? 'active' : ''}`}
                        >
                            {opt.OptionName || `Option ${idx + 1}`}
                        </button>
                    ))}
                </div>
            )}

            {/* ── Single option label (no tabs needed) ── */}
            {packageOptions.length === 1 && (
                <div className="summary-tabs-container" style={{ padding: "10px 20px" }}>
                    <span className="summary-badge hotel" style={{ padding: "2px 10px", fontSize: "11px" }}>
                        {packageOptions[0]?.OptionName || "Deluxe Package"}
                    </span>
                </div>
            )}

            {/* ── Active Option Panel ── */}
            {packageOptions.length === 0 ? (
                <div style={{ padding: "40px", textAlign: "center", color: "#adb5bd" }}>
                    No package options configured yet
                </div>
            ) : (
                <OptionSummaryPanel
                    opt={packageOptions[activeTab] || packageOptions[0]}
                    values={values}
                />
            )}

            {/* ── Disclaimer ── */}
            <div className="summary-disclaimer">
                <IconComponent icon={FaInfoCircle} className="icon" />
                <p className="text">
                    Prices shown are estimated cost-prices based on your inventory selections.
                    Final rates may vary based on real-time availability and applicable taxes at the time of booking.
                </p>
            </div>
        </ShadowCard>
    );
};

export default QuotationSummary;